import { getEntityLabel, type EntityCopyKey } from './entities';

export const errorMessages = {
  'field.required': ({ label }: { label: string }) => `${label} is required`,
  'field.requiredFields': 'Please fill in the required fields.',
  'field.exactMatchRequired': ({ label }: { label: string }) =>
    `Enter the exact ${label} to confirm.`,
  'banner.chartLoadFailed': 'Failed to load chart data',
  'banner.historyLoadFailed': 'Failed to load history',
  'banner.metricsLoadFailed': 'Failed to load metrics',
  'banner.unexpectedError': 'Something went wrong.',
  'banner.reloadAndTryAgain': 'Something went wrong. Please reload and try again.',
  'grid.errorTitle': 'Something went wrong',
  'grid.errorBody': 'Refresh or try again later.',
  'auth.loadSessionFailed': 'Unable to load authentication session',
  'auth.refreshSessionFailed': 'Unable to refresh authentication session',
  'auth.submitFailed': 'Something went wrong. Please try again.',
  'load.failed': ({ entity }: { entity: EntityCopyKey }) =>
    `Failed to load ${getEntityLabel(entity)}`,
  'load.namedFailed': ({ label }: { label: string }) => `Failed to load ${label}`,
  'load.namedUnexpected': ({ label }: { label: string }) =>
    `Something went wrong while fetching ${label}.`,
  'mutation.deleteFailed': ({ entity }: { entity: EntityCopyKey }) =>
    `Failed to delete ${getEntityLabel(entity)}`,
  'mutation.actionFailed': ({ action, target }: { action: string; target: string }) =>
    `Failed to ${action} ${target}`,
  'mutation.submitFailed': ({ target }: { target: string }) => `Failed to submit ${target}`,
  'upload.missingRequiredHeaders': ({ headers }: { headers: string }) =>
    `Missing required headers: ${headers}.`,
  'upload.selectProject': 'Select a project for this upload.',
  'upload.selectSupportedCoverageFile': 'Select at least one supported coverage file.',
  'upload.selectSupportedFile': 'Select at least one supported file to upload.',
  'upload.readSpreadsheetFailed': 'Unable to read CSV/XLSX file.',
  'retry.tryAgain': 'Try again',
} as const;

export type ErrorMessageCatalog = typeof errorMessages;
export type ErrorMessageKey = keyof ErrorMessageCatalog;

export type ErrorMessageArgs<K extends ErrorMessageKey> = ErrorMessageCatalog[K] extends (
  args: infer TArgs,
) => string
  ? TArgs
  : undefined;

export const getErrorCopy = <K extends ErrorMessageKey>(
  key: K,
  args?: ErrorMessageArgs<K>,
): string => {
  const entry = errorMessages[key];
  if (typeof entry === 'function') {
    return (entry as (messageArgs: ErrorMessageArgs<K>) => string)(
      args ?? ({} as ErrorMessageArgs<K>),
    );
  }
  return entry;
};

type ErrorSampleArgs = {
  [K in ErrorMessageKey]?: ErrorMessageArgs<K>;
};

const sampleArgs: ErrorSampleArgs = {
  'field.required': { label: 'Project name' },
  'field.exactMatchRequired': { label: 'project name' },
  'load.namedUnexpected': { label: 'diagnostics' },
  'load.failed': { entity: 'project' },
  'load.namedFailed': { label: 'diagnostics' },
  'mutation.deleteFailed': { entity: 'project' },
  'mutation.actionFailed': { action: 'update', target: 'user' },
  'mutation.submitFailed': { target: 'feedback' },
  'upload.missingRequiredHeaders': { headers: 'Test, Status' },
};

const getErrorCopySample = <K extends ErrorMessageKey>(
  key: K,
  args: ErrorMessageArgs<K> | undefined,
): { key: ErrorMessageKey; value: string } => ({
  key,
  value: getErrorCopy(key, args),
});

export const getErrorCopySamples = (): Array<{ key: ErrorMessageKey; value: string }> =>
  (Object.keys(errorMessages) as ErrorMessageKey[]).map((key) => ({
    key,
    value: getErrorCopySample(key, sampleArgs[key] as never).value,
  }));
